// src/Pages/PatientsAccounts/PatientFileManager.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
  forwardRef,
} from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Offcanvas,
  ListGroup,
} from "react-bootstrap";
import {
  fetchPatientFiles,
  uploadPatientFile,
  deletePatientFile,
  updatePatientFileCaption,
} from "../../api";
import "./PatientFileManager.css";
import "../Accounts/acc-list.css";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // matches backend fileStorageRoutes multer cap

function fileExtOf(name = "") {
  const ext = name.split(".").pop()?.toLowerCase();
  return ext || "";
}
function isImage(file) {
  const ext = fileExtOf(file?.name);
  return ["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext);
}
function isPdf(file) {
  return fileExtOf(file?.name) === "pdf";
}

// This is the per-row component, with forwardRef so parent can focus its input
const FileRow = forwardRef(function FileRow(
  {
    file,
    isActive,
    isEditing,
    saving,
    onSelect,
    onBeginRename,
    onSaveRename,
    onCancelRename,
    onDelete,
  },
  ref
) {
  const [editingValue, setEditingValue] = useState(file.caption || file.name);

  // Reset editingValue when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditingValue(file.caption || file.name);
    }
  }, [isEditing, file.caption, file.name]);

  return (
    <div
      key={file.name}
      className="acc-card small"
      // Selected file uses the brand tint rather than the solid primary fill,
      // matching SessionNoteRail: this is a picker column beside a preview, and
      // a stack of solid-blue rows reads far heavier than a file list should.
      style={{
        cursor: "pointer",
        backgroundColor: isActive ? "var(--acc-brand-tint)" : "#fff",
        borderColor: isActive ? "var(--acc-brand-primary)" : "var(--acc-border-color)",
      }}
      onMouseDown={() => onSelect(file)}
      onClick={() => onSelect(file)}
    >
      <div>
        <div className="d-flex justify-content-between align-items-center">
          <div className="w-100 min-w-0">
            {!isEditing ? (
              <div
                className="fw-semibold ellipsis-1"
                title={file.caption || file.name}
                style={{ userSelect: "none" }}
              >
                {file.caption || file.name}
                {saving && (
                  <Spinner animation="border" size="sm" className="ms-2" />
                )}
              </div>
            ) : (
              <Form.Control
                ref={ref}
                size="sm"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onSaveRename(file, editingValue);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    onCancelRename();
                  }
                }}
                onBlur={() => onSaveRename(file, editingValue)}
              />
            )}
            <div className="text-muted ellipsis-1">
              <small>
                {new Date(file.uploadedAt).toLocaleDateString("en-GB")} •{" "}
                {fileExtOf(file.name)}
              </small>
            </div>
          </div>
          <div className="flex-shrink-0 nowrap ms-2">
            {!isEditing && (
              <OverlayTrigger placement="top" overlay={<Tooltip>Rename</Tooltip>}>
                <Button
                  variant="link"
                  size="sm"
                  className="text-primary text-decoration-none"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onBeginRename(file);
                  }}
                  aria-label={`Rename ${file.caption || file.name}`}
                >
                  <i className="fa-solid fa-pen-to-square" />
                </Button>
              </OverlayTrigger>
            )}
            <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
              <Button
                variant="link"
                size="sm"
                className="text-danger text-decoration-none"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(file);
                }}
                aria-label={`Delete ${file.caption || file.name}`}
              >
                <i className="fa-solid fa-trash" />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>
    </div>
  );
});

// Memoize FileRow
const MemoFileRow = memo(
  FileRow,
  (prev, next) =>
    prev.file === next.file &&
    prev.isActive === next.isActive &&
    prev.isEditing === next.isEditing &&
    prev.saving === next.saving
);

export default function PatientFileManager({ patientId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);

  // inline rename state
  const [editingId, setEditingId] = useState(null); // file.name being edited
  const [editingValue, setEditingValue] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [compressing, setCompressing] = useState(null); // { fileName, attempt, attempts, done, total } | null

  // input ref that will be forwarded to the editing row
  const inputRef = useRef(null);

  // mobile / drawer stuff
  const [isMobile, setIsMobile] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [query, setQuery] = useState("");
  const fileInputRef = useRef(null);

  // detect mobile
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const list = await fetchPatientFiles(patientId);
      setFiles(list || []);
      if (list?.length) {
        setSelected((prev) => {
          if (!prev) return list[0];
          const found = list.find((f) => f.name === prev.name);
          return found || list[0];
        });
      } else {
        setSelected(null);
      }
    } catch (e) {
      setError("Failed to load files.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (patientId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // When entering edit mode, focus & select the text
 useEffect(() => {
    if (editingId != null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  

  const onChooseFiles = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    setUploading(true);
    setError("");
    const failures = [];
    try {
      for (const original of fileList) {
        let file = original;
        // Oversized PDFs are run through the rasterise-and-rebuild compressor
        // before upload. The compressor is dynamically imported so pdfjs +
        // pdf-lib only enter the bundle when someone actually exceeds the cap.
        if (isPdf(file) && file.size > MAX_UPLOAD_BYTES) {
          try {
            const { compressPdf } = await import("./compressPdf");
            setCompressing({ fileName: file.name, attempt: 1, attempts: 1 });
            const compressed = await compressPdf(file, {
              targetBytes: MAX_UPLOAD_BYTES,
              onProgress: (p) =>
                setCompressing({ fileName: file.name, ...p }),
            });
            if (compressed.size > MAX_UPLOAD_BYTES) {
              failures.push(
                `${file.name} is still ${(compressed.size / 1024 / 1024).toFixed(1)}MB after compression — skipped.`,
              );
              continue;
            }
            file = compressed;
          } catch (cErr) {
            console.error("PDF compression failed:", cErr);
            failures.push(`${file.name} could not be compressed — skipped.`);
            continue;
          } finally {
            setCompressing(null);
          }
        } else if (file.size > MAX_UPLOAD_BYTES) {
          failures.push(
            `${file.name} is over ${MAX_UPLOAD_BYTES / 1024 / 1024}MB — skipped.`,
          );
          continue;
        }
        await uploadPatientFile({ file, caption: file.name, patientId });
      }
      await load();
      if (failures.length) setError(failures.join(" "));
    } catch (e) {
      setError("Upload failed. Please try again.");
      console.error(e);
    } finally {
      setUploading(false);
      setCompressing(null);
      e.target.value = "";
    }
  };

  const onDelete = async (file) => {
    if (!window.confirm(`Delete "${file?.name}"?`)) return;
    try {
      await deletePatientFile(file.name);
      await load();
    } catch (e) {
      alert("Delete failed");
    }
  };

  // Handlers memoized
 const handleSelect = useCallback((f) => setSelected(f), []);
  const handleBeginRename = useCallback((f) => {
    setEditingValue(f.caption || f.name);
    setEditingId(f.name);
  }, []);
  const cancelRename = useCallback(() => {
    setEditingId(null);
  }, []);
  const saveRename = useCallback(
    async (file, newCaption) => {
      newCaption = (newCaption || "").trim();
      if (!newCaption || newCaption === (file.caption || file.name)) {
        cancelRename();
        return;
      }
      try {
        setSavingId(file.name);
        await updatePatientFileCaption(file.name, newCaption);
        // Optimistic update
        setFiles((prev) =>
          prev.map((f) =>
            f.name === file.name ? { ...f, caption: newCaption } : f
          )
        );
        setSelected((prev) =>
          prev?.name === file.name ? { ...prev, caption: newCaption } : prev
        );
      } catch (e) {
        alert("Could not rename file.");
        console.error(e);
      } finally {
        setSavingId(null);
        cancelRename();
      }
    },
    [cancelRename]
  );
  const handleChangeEditValue = useCallback((val) => {
    setEditingValue(val);
  }, []);
  const handleKeyDownEdit = useCallback(
    (file, e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        saveRename(file);
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelRename();
      }
    },
    [editingValue] // you'll include other deps if needed
  );
  const handleBlurEdit = useCallback(
    (file) => {
      saveRename(file);
    },
    [editingValue]
  );

  

  // filter for mobile list
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => {
      const cap = (f.caption || f.name || "").toLowerCase();
      return cap.includes(q);
    });
  }, [files, query]);

  // Preview pane (image / pdf / fallback)
  const Preview = useMemo(() => {
    if (!selected) return null;
    if (isImage(selected)) {
      return (
        <img
          src={selected.imageUrl}
          alt={selected.caption || selected.name}
          style={{
            maxWidth: "100%",
            maxHeight: 600,
            width: "100%",
            objectFit: "contain",
          }}
        />
      );
    }
    if (isPdf(selected)) {
      return (
        <iframe
          title={selected.name}
          src={selected.imageUrl}
          style={{
            width: "100%",
            height: 600,
            border: "1px solid rgba(0,0,0,.1)",
          }}
        />
      );
    }
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center border rounded p-4 bg-light"
        style={{ height: 300 }}
      >
        <div className="text-muted mb-2">
          Preview not available for this file type ({fileExtOf(selected.name)}).
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={() => window.open(selected.imageUrl, "_blank")}
          >
            Open
          </Button>
          <a
            className="btn btn-outline-secondary"
            href={selected.imageUrl}
            download
          >
            Download
          </a>
        </div>
      </div>
    );
  }, [selected]);

  const Desktop = () => (
    <>
      <Row className="align-items-center mb-3">
        <Col md={8}>
          <p className="text-muted mb-0">View existing files and upload more. When renaming a file press ENTER to save.</p>
        </Col>
        <Col md={4} className="text-md-end mt-2 mt-md-0">
          <Form.Label className="btn btn-outline-primary mb-0">
            {compressing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Compressing PDF…
              </>
            ) : uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Uploading…
              </>
            ) : (
              <>
                <i className="bi bi-upload me-2" /> Upload
              </>
            )}
            <Form.Control
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              onChange={onChooseFiles}
              disabled={uploading}
              style={{ display: "none" }}
            />
          </Form.Label>
        </Col>
      </Row>

      {compressing && (
        <Alert variant="info" className="py-2">
          Compressing {compressing.fileName}
          {compressing.total ? ` — page ${compressing.done}/${compressing.total}` : "…"}
          {compressing.attempts > 1 ? ` (pass ${compressing.attempt}/${compressing.attempts})` : ""}
        </Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        <Col md={4} className="mb-3">
          {/* A single-column <Table> of one-cell rows was only ever a card list
              wearing table markup. `.acc-list` supplies the surface the cards
              are scoped to. */}
          <div className="acc-list">
            {loading ? (
              <div className="text-center py-3">
                <Spinner size="sm" className="me-2" /> Loading…
              </div>
            ) : files.length ? (
              files.map((f) => (
                <MemoFileRow
                  key={f.name}
                  file={f}
                  isActive={selected?.name === f.name}
                  isEditing={editingId === f.name}
                  saving={savingId === f.name}
                  onSelect={handleSelect}
                  onBeginRename={handleBeginRename}
                  onSaveRename={saveRename}
                  onCancelRename={cancelRename}
                  onDelete={onDelete}
                  ref={editingId === f.name ? inputRef : null}
                />
              ))
            ) : (
              <p className="text-muted small mb-0">No files yet.</p>
            )}
          </div>
        </Col>
        <Col md={8}>
          {selected ? (
            <div className="bg-white border rounded p-3 shadow-sm">
              <div className="preview-header mb-3">
                <div className="min-w-0" style={{ maxWidth: "100%" }}>
                  <div
                    className="title"
                    title={selected.caption || selected.name}
                  >
                    {selected.caption || selected.name}
                  </div>
                  <div className="text-muted small">
                    {fileExtOf(selected.name)} •{" "}
                    {new Date(selected.uploadedAt).toLocaleString("en-GB")}
                  </div>
                </div>
                <div className="flex-shrink-0 nowrap ms-2">
                  <a
                    className="btn btn-outline-secondary btn-sm ms-2 rounded-0"
                    href={selected.imageUrl}
                    download
                  >
                    <i className="fa-solid fa-share-from-square"></i>
                  </a>
                </div>
              </div>
              {Preview}
            </div>
          ) : (
            <div className="text-muted">Select a file to preview.</div>
          )}
        </Col>
      </Row>
    </>
  );

  const Mobile = () => (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="min-w-0">
          <small className="text-muted"></small>
        </div>
        <div className="d-flex gap-2 flex-shrink-0 nowrap">
          <Button
            variant="outline-primary"
            size="sm"
            className="rounded-0"
            onClick={() => setShowPicker(true)}
            aria-label="Choose file"
          >
            <i className="fa-regular fa-file me-2"></i>
            Files ({files.length})
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="rounded-0"
            onClick={() => fileInputRef.current?.click()}
          >
            {compressing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Compressing…
              </>
            ) : uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Uploading…
              </>
            ) : (
              <>
                <i className="bi bi-upload me-2" /> Upload
              </>
            )}
          </Button>
          <Form.Control
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            onChange={onChooseFiles}
            disabled={uploading}
            style={{ display: "none" }}
          />
        </div>
      </div>

      {compressing && (
        <Alert variant="info" className="mb-2 py-2">
          Compressing {compressing.fileName}
          {compressing.total ? ` — page ${compressing.done}/${compressing.total}` : "…"}
          {compressing.attempts > 1 ? ` (pass ${compressing.attempt}/${compressing.attempts})` : ""}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mb-2">
          {error}
        </Alert>
      )}

      <div className="bg-white border rounded p-3 shadow-sm">
        {selected ? (
          <>
            <div className="preview-header mb-2">
              <div className="min-w-0" style={{ maxWidth: "100%" }}>
                <div
                  className="title"
                  title={selected.caption || selected.name}
                >
                  {selected.caption || selected.name}
                </div>
                <div className="text-muted small">
                  {fileExtOf(selected.name)} •{" "}
                  {new Date(selected.uploadedAt).toLocaleString("en-GB")}
                </div>
              </div>
              <div className="flex-shrink-0 nowrap ms-2">
                <a
                  className="btn btn-outline-secondary btn-sm ms-2 rounded-0"
                  href={selected.imageUrl}
                  download
                >
                  <i className="fa-solid fa-share-from-square"></i>
                </a>
              </div>
            </div>
            {Preview}
          </>
        ) : loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="text-muted">No files yet. Tap Upload to add.</div>
        )}
      </div>

      <Offcanvas
        show={showPicker}
        onHide={() => setShowPicker(false)}
        placement="bottom"
        scroll
        backdrop
        className="files-drawer"
        style={{ height: "50vh" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="w-100">
            <div className="d-flex align-items-center justify-content-between w-100">
              <span>Files</span>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : filtered.length ? (
            <ListGroup
              variant="flush"
              style={{ maxHeight: "calc(55vh - 110px)", overflowY: "auto" }}
            >
              {filtered.map((f) => {
                const isActive = selected?.name === f.name;
                const isEditing = editingId === f.name;
                return (
                  <ListGroup.Item
                    key={f.name}
                    action
                    onClick={() => {
                      if (isEditing) return;
                      setSelected(f);
                      setShowPicker(false);
                    }}
                    className={`${isActive ? "bg-light" : ""}`}
                  >
                    <div className="file-row">
                      <div className="file-main min-w-0 pe-2">
                        {!isEditing ? (
                          <div
  className="file-title ellipsis-1"
  title={f.caption || f.name}
  style={{ userSelect: "none" }}
>
  {f.caption || f.name}
  {savingId === f.name && (
    <Spinner animation="border" size="sm" className="ms-2" />
  )}
</div>
                        ) : (
                          <Form.Control
                            ref={inputRef}
                            size="sm"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                saveRename(f, editingValue);
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                cancelRename();
                              }
                            }}
                            onBlur={() => saveRename(f, editingValue)}
                            className="mt-1"
                          />
                        )}
                        <div className="file-meta small ellipsis-1">
                          {new Date(f.uploadedAt).toLocaleDateString("en-GB")} •{" "}
                          {fileExtOf(f.name)}
                        </div>
                      </div>
                      <div className="file-actions">
                        {!isEditing && (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-secondary text-decoration-none ms-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBeginRename(f);
                            }}
                            title="Rename"
                            aria-label={`Rename ${f.caption || f.name}`}
                          >
                            <i className="bi bi-pencil-fill" />
                          </Button>
                        )}
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger text-decoration-none ms-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(f);
                          }}
                          title="Delete"
                          aria-label={`Delete ${f.caption || f.name}`}
                        >
                          <i className="bi bi-trash-fill" />
                        </Button>
                      </div>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          ) : (
            <div className="text-muted">No files found.</div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );

  return isMobile ? <Mobile /> : <Desktop />;
}
